import { useState } from 'react'
import useSWR from 'swr'
import { api, fetcher } from '../api/client'

const PROVIDERS = ['Tavily', 'Serper', 'GoogleCse', 'Brave', 'Mock']

export default function AdminSearchConfigPage() {
    const { data, mutate } = useSWR<any>('/api/search-providers', fetcher)
    const { data: active } = useSWR<any>('/api/search-providers/active', fetcher)

    const [provider, setProvider] = useState('Tavily')
    const [apiKey, setApiKey] = useState('')
    const [cxOrEngineId, setCxOrEngineId] = useState('')
    const [resultsPerQuery, setResultsPerQuery] = useState(8)
    const [lookbackDays, setLookbackDays] = useState(365)
    const [busy, setBusy] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)

    async function activate() {
        setBusy(true); setMsg(null)
        try {
            await api('/api/search-providers/active', {
                method: 'POST',
                body: JSON.stringify({
                    provider,
                    apiKey: apiKey || null,
                    cxOrEngineId: cxOrEngineId || null,
                    resultsPerQuery, lookbackDays
                })
            })
            setMsg(`${provider} is now the active search provider.`)
            setApiKey('')
            mutate()
        } catch (e: any) { setMsg(`Error: ${e.message}`) }
        finally { setBusy(false) }
    }

    return (
        <div>
            <h1>Search Provider Configuration</h1>
            <p className="muted">Search powers the recent-news + decision-maker discovery in enrichment. Tavily is recommended for dev (free tier, LLM-friendly snippets).</p>

            <div className="card">
                <h2>Active Provider</h2>
                {active?.active === false ? <p className="muted">No search provider configured. Enrichment falls back to website-only scraping.</p> :
                    active && <p>
                        <strong>{active.providerName}</strong> · {active.resultsPerQuery} results/query · last {active.lookbackDays} days
                    </p>
                }
            </div>

            <div className="card">
                <h2>Set Active Provider</h2>
                <div className="split">
                    <div className="form-row">
                        <label>Provider</label>
                        <select value={provider} onChange={e => setProvider(e.target.value)}>
                            {PROVIDERS.map(p => <option key={p}>{p}</option>)}
                        </select>
                    </div>
                    <div className="form-row">
                        <label>API Key (leave blank to keep existing)</label>
                        <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={provider === 'Tavily' ? 'tvly-...' : 'API key'} />
                    </div>
                    {provider === 'GoogleCse' && (
                        <div className="form-row">
                            <label>CX (Custom Search Engine ID)</label>
                            <input value={cxOrEngineId} onChange={e => setCxOrEngineId(e.target.value)} />
                        </div>
                    )}
                    <div className="form-row">
                        <label>Results per query</label>
                        <input type="number" min="1" max="20" value={resultsPerQuery} onChange={e => setResultsPerQuery(parseInt(e.target.value) || 8)} />
                    </div>
                    <div className="form-row">
                        <label>Lookback window (days, {lookbackDays})</label>
                        <input type="range" min="30" max="730" step="30" value={lookbackDays} onChange={e => setLookbackDays(parseInt(e.target.value))} />
                    </div>
                </div>
                {msg && <div className={msg.startsWith('Error') ? 'error' : 'success'}>{msg}</div>}
                <button onClick={activate} disabled={busy} data-testid="activate-search-provider">Activate {provider}</button>
            </div>

            <div className="card">
                <h2>All Configured Search Providers</h2>
                {!data?.length ? <p className="muted">None configured yet.</p> :
                    <table>
                        <thead><tr><th>Provider</th><th>Results/Q</th><th>Lookback</th><th>Active</th></tr></thead>
                        <tbody>
                            {data.map((c: any) => (
                                <tr key={c.providerName}>
                                    <td>{c.providerName}</td>
                                    <td>{c.resultsPerQuery}</td>
                                    <td>{c.lookbackDays} days</td>
                                    <td>{c.isActive ? '✓' : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                }
            </div>
        </div>
    )
}
